import { useState, useEffect } from 'react';
import { AtSign, Bot } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { motion } from 'framer-motion';

const AutomatedLeadHandlingSection = () => {
  const isMobile = useIsMobile();
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <section className="section relative py-24 overflow-hidden">
      {/* Background gradient effect */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-pink-900/10 to-transparent" />
      
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="max-w-6xl mx-auto px-6 relative"
      >
        <div className="flex flex-col space-y-8">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Feature badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-pink-500/10 to-rose-500/10 backdrop-blur-sm border border-pink-500/20">
              <Bot className="w-4 h-4 text-pink-400" />
              <span className="text-sm font-medium bg-gradient-to-r from-pink-400 to-rose-400 bg-clip-text text-transparent">
                Real-Time Automation
              </span>
            </div>
            
            {/* Icon with glow effect */}
            <motion.div 
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ duration: 0.5, type: "spring" }}
              viewport={{ once: true }}
              className="relative inline-block"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-pink-500 to-rose-500 blur-xl opacity-50" />
              <div className="relative bg-gradient-to-br from-pink-500/20 to-rose-500/20 backdrop-blur-sm border border-pink-500/30 p-4 rounded-2xl">
                <AtSign size={isMobile ? 28 : 32} className="text-white" />
              </div>
            </motion.div>
            
            <h3 className="text-3xl md:text-4xl font-bold">
              <span className="text-white">Automated</span>{' '}
              <span className="bg-gradient-to-r from-pink-400 via-rose-400 to-pink-400 bg-clip-text text-transparent animate-gradient-x">
                Lead Handling
              </span>
            </h3>
            
            <p className="text-xl text-gray-300 leading-relaxed max-w-3xl">
              The system automatically{' '}
              <span className="text-white font-medium">processes, analyzes, and responds to leads</span>{' '}
              in real-time, allowing you to focus on high-value interactions.
            </p>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Glow effect */}
            <div className="absolute -inset-4 bg-gradient-to-r from-pink-500/20 to-rose-500/20 blur-3xl opacity-60" />
            
            {/* Glass morphism container */}
            <div className="relative backdrop-blur-sm bg-gradient-to-br from-gray-800/40 to-gray-900/40 rounded-2xl border border-gray-700/50 p-1 shadow-2xl hover:shadow-pink-500/20 transition-all duration-300">
              <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 rounded-xl overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-tr from-pink-500/5 to-rose-500/5" />
                <div className="relative">
                  <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-8 h-[600px] flex items-center justify-center rounded-xl">
                    <div className="text-center space-y-6">
                      <motion.div
                        className="w-16 h-16 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full mx-auto flex items-center justify-center"
                        animate={{ 
                          scale: [1, 1.1, 1],
                          rotate: [0, 180, 360]
                        }}
                        transition={{ 
                          duration: 4, 
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      >
                        <AtSign className="w-8 h-8 text-white" />
                      </motion.div>
                      <h4 className="text-2xl font-bold text-white">Automated Lead Processing</h4>
                      <p className="text-gray-300 max-w-md">
                        Watch as AI automatically analyzes, categorizes, and responds to incoming leads in real-time.
                      </p>
                      <div className="flex justify-center space-x-2">
                        {[0, 1, 2].map((i) => (
                          <motion.div
                            key={i}
                            className="w-3 h-3 bg-pink-500 rounded-full"
                            animate={{ 
                              scale: [0.8, 1.2, 0.8],
                              opacity: [0.5, 1, 0.5]
                            }}
                            transition={{ 
                              duration: 1.5, 
                              repeat: Infinity,
                              delay: i * 0.3
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
};

export default AutomatedLeadHandlingSection;
